import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, Upload, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const CLASSIFICATION_TONE = {
  public: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  private: 'bg-sky-100 text-sky-800 border-sky-200',
  internal: 'bg-amber-100 text-amber-800 border-amber-200',
};

// Non-KR managers cannot set `public` classification (backend enforces).
function allowedClassifications(isKR) {
  return isKR ? ['public', 'private', 'internal'] : ['private', 'internal'];
}

export default function AttachmentsPanel({
  event, attachments, canManage, isKR, onChanged,
}) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, row, currentFlyer? }

  const eventId = event?.event_id;

  // Phase 27.30.10 — Auth-aware download. Previously `<a href={apiUrl}
  // target="_blank">` opened the API endpoint directly and the browser
  // downloaded the "Not authenticated" JSON reply because it did not
  // send the Authorization header. Fetch with the token, wrap the
  // response in a blob URL, then trigger the download click.
  const downloadAttachmentFile = async (att) => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/events/${eventId}/attachments/${att.attachment_id}/download`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        let detail = '';
        try { const j = await r.json(); detail = j?.detail || ''; } catch { /* not JSON */ }
        throw new Error(detail || `Download failed (HTTP ${r.status})`);
      }
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a beat to hand the blob to the download handler
      // before we revoke it — otherwise Safari can drop the file.
      setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
    } catch (err) {
      toast.error(err?.message || 'Download failed');
    }
  };
  const canUpload = canManage && event && event.status !== 'archived';
  const currentFlyer = (attachments || []).find((a) => a.is_flyer);

  const refresh = () => onChanged?.();

  const handleClassificationChange = async (row, newValue) => {
    if (newValue === row.classification) return;
    if (newValue === 'public' && !isKR) {
      toast.error('Only KR staff can set classification to public');
      return;
    }
    setBusyId(row.attachment_id);
    try {
      // If a flyer is public and moves off public, clear is_flyer too.
      const patch = { classification: newValue };
      if (row.is_flyer && newValue !== 'public') patch.is_flyer = false;
      await calendarAPI.updateAttachment(eventId, row.attachment_id, patch);
      toast.success('Classification updated');
      refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (row) => {
    setBusyId(row.attachment_id);
    try {
      await calendarAPI.deleteAttachment(eventId, row.attachment_id);
      toast.success('Attachment removed');
      refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const doPromoteFlyer = async (row) => {
    setBusyId(row.attachment_id);
    try {
      await calendarAPI.updateAttachment(eventId, row.attachment_id, { is_flyer: true });
      toast.success('Promoted to canonical flyer');
      refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Promote failed'));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  return (
    <section data-testid="attachments-panel">
      <div className="flex items-center gap-2 mb-2">
        <Paperclip size={14} className="text-slate-500" />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Attachments</h4>
        <span className="text-xs text-slate-500">{attachments?.length || 0}</span>
        {canUpload && (
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => setUploadOpen(true)}
            data-testid="attachments-upload-btn"
          >
            <Upload size={14} className="mr-1" /> Upload
          </Button>
        )}
      </div>

      {(attachments || []).length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="attachments-empty">
          No files.
        </p>
      ) : (
        <ul className="space-y-1" data-testid="attachments-list">
          {attachments.map((a) => {
            const isBusy = busyId === a.attachment_id;
            return (
              <li
                key={a.attachment_id}
                className="p-2 rounded border border-slate-200 flex items-center gap-2"
                data-testid={`attachment-row-${a.attachment_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-800 truncate">{a.filename}</span>
                    {a.is_flyer && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200" data-testid={`attachment-flyer-badge-${a.attachment_id}`}>
                        <Star size={10} className="mr-0.5" /> Canonical flyer
                      </Badge>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                    {a.mime_type || '—'} · {formatSize(a.size_bytes)}
                  </div>
                </div>

                {/* Classification: inline select for managers, badge for others */}
                {canManage ? (
                  <Select
                    value={a.classification}
                    onValueChange={(v) => handleClassificationChange(a, v)}
                    disabled={isBusy}
                  >
                    <SelectTrigger
                      className="h-7 w-28 text-xs"
                      data-testid={`attachment-classification-${a.attachment_id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedClassifications(isKR).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                      {/* If the row is already `public` and viewer isn't KR,
                          keep the value visible even though it can't be re-selected. */}
                      {!isKR && a.classification === 'public' && (
                        <SelectItem value="public" disabled>public (KR only)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`text-[10px] border ${CLASSIFICATION_TONE[a.classification] || ''}`}>
                    {a.classification}
                  </Badge>
                )}

                <button
                  type="button"
                  onClick={() => downloadAttachmentFile(a)}
                  className="text-xs text-sky-600 hover:underline shrink-0"
                  data-testid={`attachment-download-${a.attachment_id}`}
                >
                  Download
                </button>

                {canManage && (
                  <>
                    {a.classification === 'public' && !a.is_flyer && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs shrink-0"
                        onClick={() => setConfirm({ kind: 'promote', row: a, currentFlyer })}
                        disabled={isBusy}
                        data-testid={`attachment-promote-${a.attachment_id}`}
                        title={currentFlyer ? 'Replace current canonical flyer' : 'Promote to canonical flyer'}
                      >
                        <Star size={12} className="mr-1" />
                        {currentFlyer ? 'Replace flyer' : 'Set as flyer'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-rose-600 shrink-0"
                      onClick={() => setConfirm({ kind: 'delete', row: a })}
                      disabled={isBusy}
                      data-testid={`attachment-delete-${a.attachment_id}`}
                      aria-label="Delete attachment"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <UploadAttachmentDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        eventId={eventId}
        isKR={isKR}
        onDone={refresh}
      />

      <AlertDialog
        open={!!confirm}
        onOpenChange={(o) => !o && !busyId && setConfirm(null)}
      >
        <AlertDialogContent data-testid="attachment-confirm-dialog">
          {confirm?.kind === 'delete' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete attachment?</AlertDialogTitle>
                <AlertDialogDescription>
                  <strong>{confirm.row.filename}</strong> will be soft-deleted and removed from the
                  event view. Downloads will stop working. This cannot be undone from the UI.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={!!busyId}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={(e) => { e.preventDefault(); doDelete(confirm.row); }}
                  disabled={!!busyId}
                  data-testid="attachment-confirm-delete"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {confirm?.kind === 'promote' && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirm.currentFlyer ? 'Replace canonical flyer?' : 'Set canonical flyer?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm.currentFlyer ? (
                    <>
                      <div className="mb-2">
                        The current canonical flyer{' '}
                        <strong>{confirm.currentFlyer.filename}</strong> will be un-flagged.
                        Public links pointing to <code>/flyer</code> will now redirect to:
                      </div>
                      <div className="text-xs bg-slate-50 border rounded p-2 font-mono">
                        {confirm.row.filename}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>{confirm.row.filename}</strong> will be marked as the canonical flyer.
                      Public <code>/flyer</code> requests for this event will 307-redirect to it.
                    </>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={!!busyId}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); doPromoteFlyer(confirm.row); }}
                  disabled={!!busyId}
                  data-testid="attachment-confirm-promote"
                >
                  {confirm.currentFlyer ? 'Replace flyer' : 'Set as flyer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function formatSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Upload dialog ─────────────────────────────────────────────

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB — matches typical PDF/flyer size

function UploadAttachmentDialog({ open, onClose, eventId, isKR, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [classification, setClassification] = useState(isKR ? 'public' : 'private');
  const [isFlyer, setIsFlyer] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setClassification(isKR ? 'public' : 'private');
    setIsFlyer(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    onClose?.();
    setTimeout(reset, 200);
  };

  const upload = async () => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File too large — max ${(MAX_UPLOAD_BYTES / (1024 * 1024)).toFixed(0)} MB`);
      return;
    }
    if (isFlyer && classification !== 'public') {
      toast.error('Canonical flyer requires classification=public');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('classification', classification);
      fd.append('is_flyer', String(isFlyer));
      await calendarAPI.uploadAttachment(eventId, fd);
      toast.success('Attachment uploaded');
      onDone?.();
      handleClose();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  // If viewer is not KR, force classification off public.
  React.useEffect(() => {
    if (!isKR && classification === 'public') {
      setClassification('private');
      setIsFlyer(false);
    }
  }, [isKR, classification]);

  // If classification changes off public, clear is_flyer.
  React.useEffect(() => {
    if (classification !== 'public' && isFlyer) setIsFlyer(false);
  }, [classification, isFlyer]);

  const allowed = allowedClassifications(isKR);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="attachment-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload attachment</DialogTitle>
          <DialogDescription>
            Choose a file (max 15 MB), pick its classification, and optionally mark it as the
            canonical flyer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="attachment-file" className="text-xs text-slate-600">File</Label>
            <input
              ref={inputRef}
              id="attachment-file"
              type="file"
              className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-900 file:text-white file:text-xs hover:file:bg-slate-800 cursor-pointer"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="attachment-upload-file-input"
            />
            {file && (
              <div className="text-[11px] text-slate-500 mt-1">
                {file.name} · {formatSize(file.size)}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-slate-600">Classification</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger className="mt-1" data-testid="attachment-upload-classification">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowed.map((c) => (
                  <SelectItem key={c} value={c}>{classificationLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">
              {classification === 'public' && 'Visible to any authenticated viewer of the event.'}
              {classification === 'private' && 'Visible only to managers and invited/accepted participants.'}
              {classification === 'internal' && 'Visible only to KR staff and the event creator.'}
            </p>
          </div>

          {classification === 'public' && (
            <label
              className="flex items-center gap-2 text-sm text-slate-700"
              data-testid="attachment-upload-is-flyer-row"
            >
              <Checkbox
                checked={isFlyer}
                onCheckedChange={(v) => setIsFlyer(!!v)}
                data-testid="attachment-upload-is-flyer"
              />
              Set as canonical flyer (replaces any existing one)
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={busy} data-testid="attachment-upload-cancel">
            Cancel
          </Button>
          <Button
            onClick={upload}
            disabled={!file || busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="attachment-upload-submit"
          >
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function classificationLabel(c) {
  return {
    public: 'Public — event viewers',
    private: 'Private — participants + managers',
    internal: 'Internal — KR + creator only',
  }[c] || c;
}
